import { ArrowUpRight, Mail, Phone, Facebook, Instagram, MessageCircle } from "lucide-react";
import { Reveal, MaskLines, SectionLabel } from "./Reveal";
import { useLang, CONTACT } from "../i18n";

export const Contact = () => {
  const { t, lang, wa: waBase } = useLang();
  const wa = `${waBase}?text=${encodeURIComponent(t.contact.waMsg)}`;
  const rows = [
    [Mail, t.contact.email, CONTACT.email, `mailto:${CONTACT.email}`, "contact-email-link"],
    [Phone, t.contact.phone, CONTACT.phone, `tel:${CONTACT.phoneRaw}`, "contact-phone-link"],
    [MessageCircle, t.contact.whatsappTn, CONTACT.phoneTn, `${CONTACT.whatsappTn}?text=${encodeURIComponent(t.contact.waMsg)}`, "contact-whatsapp-tn-link"],
    [Facebook, t.contact.facebook, "TSM WAY", CONTACT.facebook, "contact-facebook-link"],
    [Instagram, t.contact.instagram, "@tsm.way", CONTACT.instagram, "contact-instagram-link"],
  ];
  return (
    <section id="contact" className="relative py-16 md:py-40 bg-[#0B0A08] overflow-hidden" data-testid="contact-section">
      <img src="/img/real2.jpg" alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0B0A08] via-[#0B0A08]/70 to-[#0B0A08]" />
      <div className="relative max-w-[1440px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-7">
          <Reveal><SectionLabel index="07">{t.contact.label}</SectionLabel></Reveal>
          <h2 key={lang} className="mt-8 font-display text-5xl sm:text-6xl lg:text-8xl leading-[0.95] text-[#F7F4EE]">
            <MaskLines lines={[t.contact.title]} />
          </h2>
          <Reveal delay={0.1}><p className="mt-8 text-base text-[#CFC6B6] max-w-lg leading-relaxed">{t.contact.text}</p></Reveal>
          <Reveal delay={0.2}>
            <a href={wa} target="_blank" rel="noreferrer" className="btn-gold mt-10 text-sm !px-8 !py-4" data-testid="contact-whatsapp-btn">
              <MessageCircle size={18} /> {t.contact.whatsapp}
            </a>
          </Reveal>
        </div>
        <div className="lg:col-span-5 flex flex-col justify-end">
          <div className="border-t border-[#D4AF37]/20">
            {rows.map(([Icon, label, val, href, tid], i) => (
              <Reveal key={tid} delay={i * 0.08}>
                <a href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noreferrer" data-testid={tid}
                  className="group flex items-center justify-between gap-4 py-7 border-b border-[#D4AF37]/20">
                  <span className="flex items-center gap-5 min-w-0">
                    <Icon size={20} className="text-[#D4AF37] shrink-0" strokeWidth={1.4} />
                    <span className="min-w-0">
                      <span className="block font-mono text-[11px] uppercase tracking-[0.25em] text-[#B5AC9C]">{label}</span>
                      <span className="block mt-1 font-display text-2xl sm:text-3xl text-[#F7F4EE] break-words pl-0.5 transition-colors duration-300 group-hover:text-[#F3E5AB]">{val.startsWith("+") ? <><span style={{ fontFamily: "Manrope, sans-serif", fontWeight: 300 }}>+</span>{val.slice(1)}</> : val}</span>
                    </span>
                  </span>
                  <ArrowUpRight className="text-[#D4AF37] shrink-0 transition-transform duration-500 group-hover:rotate-45" />
                </a>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export const Footer = () => {
  const { t } = useLang();
  return (
    <footer className="bg-[#0B0A08] border-t border-[#D4AF37]/15 overflow-hidden" data-testid="footer">
      <div className="max-w-[1440px] mx-auto px-5 md:px-10 pt-16 pb-8">
        <div className="flex flex-col md:flex-row md:items-end gap-10 md:gap-16">
          <img src="/img/logo_full.png" alt="TSM WAY" className="w-48 md:w-64 h-auto shrink-0 drop-shadow-[0_10px_40px_rgba(212,175,55,0.25)]" data-testid="footer-logo" />
          <p className="font-display text-3xl sm:text-4xl md:text-5xl leading-[1.05] text-[#F7F4EE]/90 max-w-2xl">{t.contact.title}</p>
        </div>
        <div className="mt-10 flex flex-col sm:flex-row justify-between gap-3 font-mono text-[11px] uppercase tracking-[0.2em] text-[#B5AC9C]">
          <span>© {new Date().getFullYear()} TSM WAY — {t.footer.rights}</span>
          <span className="flex gap-6 items-center"><span>{t.footer.tagline}</span>
            <a href={CONTACT.instagram} target="_blank" rel="noreferrer" aria-label="Instagram" className="hover:text-[#F3E5AB] transition-colors" data-testid="footer-instagram-link"><Instagram size={16} strokeWidth={1.5} /></a>
            <a href={CONTACT.facebook} target="_blank" rel="noreferrer" aria-label="Facebook" className="hover:text-[#F3E5AB] transition-colors" data-testid="footer-facebook-link"><Facebook size={16} strokeWidth={1.5} /></a>
            <a href="/admin" className="hover:text-[#F3E5AB] transition-colors" data-testid="footer-admin-link">{t.footer.admin}</a></span>
        </div>
      </div>
    </footer>
  );
};
